import cx from "classnames";
import React from "react";
import { FormErrors } from "redux-form";

import { ReviewRating } from "@/mk2/apps/bazaar/schemas";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { Form } from "@/mk2/components/Form";
import { SelectField } from "@/mk2/components/forms/SelectField";
import { SubmitButton } from "@/mk2/components/forms/SubmitButton";
import { TextareaField } from "@/mk2/components/forms/TextareaField";
import { UploadedPhoto } from "@/mk2/containers/PhotosUpload/PhotosUpload";
import { ComposeToolbar, FIXED } from "@/mk2/containers/Toolbar/Toolbar";
import { MKReduxFormInjectedProps, mkReduxForm } from "@/mk2/decorators/mkReduxForm";
import { memoizeOne } from "@/mk2/helpers/cache";
import {
    BZ2_SEND_REVIEW,
    CANCEL,
    FORMS_DEFAULT_REQUIRED_FIELD_ERROR,
    NEW_REVIEW,
    REVIEW_RATING_LABEL_BUYER,
    REVIEW_RATING_LABEL_SELLER,
    REVIEW_RATING_UNSATISFIED,
    REVIEW_RATING_VERY_SATISFIED,
    SELECT,
} from "@/mk/autogenerated/translations/ReviewForm.30de1600605cd772ae6dfa458e5d0935";

import styles from "./ReviewForm.mscss";

export interface ReviewFormData {
    message: string;
    rating: ReviewRating | "";
    photos: UploadedPhoto[];
}

const emojisConfig = {
    fields: ["message"],
    defaultField: "message",
};

const photosConfig = {
    photoType: "review",
    multiple: false,
    previewPhotoSize: "s150x150",
    showPhotoCode: false,
};

interface OwnProps {
    className?: string;
    initialValues: ReviewFormData;
    asBuyer: boolean;
    form: string;

    onSave(values: ReviewFormData);
    onCancel(event: React.MouseEvent<HTMLAnchorElement>);
}

type Props = OwnProps;

class ReviewForm extends React.Component<Props & MKReduxFormInjectedProps<ReviewFormData, Props>> {
    private getRatingOptions = memoizeOne((asBuyer: boolean) => {
        return [
            { value: "", label: SELECT },
            { value: ReviewRating.VERY_SATISFIED, label: REVIEW_RATING_VERY_SATISFIED },
            { value: ReviewRating.UNSATISFIED, label: REVIEW_RATING_UNSATISFIED },
        ];
    });

    public render() {
        const { className, handleSubmit, form, onCancel, asBuyer } = this.props;

        return (
            <Form className={cx(styles.ReviewForm, className)} onSubmit={handleSubmit}>
                <div className={styles.ReviewForm__rating}>
                    <SelectField
                        name="rating"
                        options={this.getRatingOptions(asBuyer)}
                        transparent
                        bottomBorder={false}
                        label={asBuyer ? REVIEW_RATING_LABEL_BUYER : REVIEW_RATING_LABEL_SELLER}
                    />
                </div>
                <div className={styles.ReviewForm__message}>
                    <TextareaField
                        name="message"
                        autoFocus
                        rows={3}
                        maxRows={5}
                        placeholder={NEW_REVIEW}
                        bottomBorder={false}
                        isCompact
                    />
                </div>

                <ComposeToolbar
                    className={styles.ReviewForm__toolbar}
                    formName={form}
                    emojis={emojisConfig}
                    photos={photosConfig}
                    stickers={false}
                    isFixed={FIXED.NEVER}
                    isInBlogs
                    rightAction={
                        <div className={styles.ReviewForm__actionButtons}>
                            <Btn
                                className={styles.ReviewForm__cancel}
                                type={BtnType.Link}
                                label={CANCEL}
                                onClick={onCancel}
                            />
                            <SubmitButton
                                className={styles.ReviewForm__submit}
                                label={BZ2_SEND_REVIEW}
                                formName={form}
                                btnType={BtnType.Blue}
                            />
                        </div>
                    }
                />
            </Form>
        );
    }
}

function validate(values: ReviewFormData): FormErrors<ReviewFormData> {
    const errors: FormErrors<ReviewFormData> = {};
    if (!values.message) {
        errors.message = FORMS_DEFAULT_REQUIRED_FIELD_ERROR;
    }

    if (!values.rating) {
        errors.rating = FORMS_DEFAULT_REQUIRED_FIELD_ERROR;
    }

    return errors;
}

export default mkReduxForm<ReviewFormData, Props>({
    photosConfig,
    onValidate: validate,
    onSave: (values, props) => props.onSave(values),
})(ReviewForm);
